import React, { useEffect, useRef, useState } from 'react';

import { colorForBox } from '../helpers';
import { resolveAssetUrl } from '../signedAssetCache';
import pageStyle from '../style.module.scss';
import style from './style.module.scss';

const CropPreview = ({ src, bbox }) => {
  const canvasRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const Left = Math.min(1, Math.max(0, Number(bbox?.Left) || 0));
  const Top = Math.min(1, Math.max(0, Number(bbox?.Top) || 0));
  const Width = Math.min(1, Math.max(0.001, Number(bbox?.Width) || 0.001));
  const Height = Math.min(1, Math.max(0.001, Number(bbox?.Height) || 0.001));

  useEffect(() => {
    let cancelled = false;
    setReady(false);
    setFailed(false);

    if (!src) {
      setFailed(true);
      return undefined;
    }

    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      const canvas = canvasRef.current;
      if (!canvas) return;

      const nw = img.naturalWidth;
      const nh = img.naturalHeight;
      const sx = Math.min(nw - 1, Math.max(0, Math.floor(Left * nw)));
      const sy = Math.min(nh - 1, Math.max(0, Math.floor(Top * nh)));
      const sw = Math.max(1, Math.min(nw - sx, Math.ceil(Width * nw)));
      const sh = Math.max(1, Math.min(nh - sy, Math.ceil(Height * nh)));

      canvas.width = sw;
      canvas.height = sh;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, sw, sh);
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
      setReady(true);
    };
    img.onerror = () => {
      if (!cancelled) {
        setReady(false);
        setFailed(true);
      }
    };
    img.src = src;

    return () => {
      cancelled = true;
    };
  }, [src, Left, Top, Width, Height]);

  return (
    <div className={style.drawerCrop}>
      <canvas
        ref={canvasRef}
        className={[style.drawerCropCanvas, ready && style.drawerCropCanvasReady].filter(Boolean).join(' ')}
      />
      {!ready ? (
        <div className={style.drawerCropLoading}>
          {failed ? 'Crop unavailable' : 'Loading crop…'}
        </div>
      ) : null}
    </div>
  );
};

const DrawerBox = ({ box, onSelect }) => {
  const {
    Top, Left, Width, Height,
  } = box.BoundingBox;
  const area = Math.max(0.0001, (Width || 0) * (Height || 0));
  const zIndex = Math.min(99, Math.max(2, Math.round(2 / Math.sqrt(area))));

  return (
    <button
      type="button"
      aria-label={box.label}
      className={pageStyle.box}
      style={{
        top: `${Top * 100}%`,
        left: `${Left * 100}%`,
        width: `${Width * 100}%`,
        height: `${Height * 100}%`,
        borderColor: colorForBox(box),
        zIndex,
      }}
      onClick={() => onSelect?.(box)}
    />
  );
};

const getDrawerViewModel = (detail, focusBox, resolvedSrc) => {
  const drilledFromTile = detail?.kind === 'tile' && Boolean(focusBox?.BoundingBox);
  const cropBbox = drilledFromTile ? focusBox.BoundingBox : detail?.bbox;
  // Prefer full-res tile.url only — never webp/preview for crops.
  const rawTileUrl = detail?.tileUrl || null;

  return {
    drilledFromTile,
    cropBbox,
    rawTileUrl,
    showCrop: Boolean(resolvedSrc && cropBbox),
    showTile: Boolean(resolvedSrc && !cropBbox),
    title: drilledFromTile ? (focusBox.label || detail.title) : detail?.title,
    subtitle: drilledFromTile
      ? (detail.title || detail.subtitle)
      : detail?.subtitle,
    rows: drilledFromTile ? (focusBox.details || []) : (detail?.rows || []),
    raw: drilledFromTile ? (focusBox.raw || null) : detail?.raw,
    rawLabel: drilledFromTile ? 'AWS Rekognition' : (detail?.rawLabel || 'Raw JSON'),
  };
};

const useResolvedTileUrl = (rawTileUrl, setResolvedSrc) => {
  useEffect(() => {
    let cancelled = false;
    setResolvedSrc(null);
    if (!rawTileUrl) return undefined;

    resolveAssetUrl(rawTileUrl).then((url) => {
      if (!cancelled) setResolvedSrc(url);
    }).catch(() => {
      if (!cancelled) setResolvedSrc(null);
    });

    return () => { cancelled = true; };
  }, [rawTileUrl, setResolvedSrc]);
};

const useDrawerLifecycle = ({
  detail, open, onClose, drilledFromTile, setFocusBox, setCopied,
}) => {
  useEffect(() => {
    setFocusBox(null);
    setCopied(false);
  }, [detail, setFocusBox, setCopied]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (drilledFromTile) setFocusBox(null);
        else onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose, drilledFromTile, setFocusBox]);
};

const copyRawJson = async (raw, setCopied) => {
  if (!raw) return;
  try {
    await navigator.clipboard.writeText(JSON.stringify(raw, null, 2));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  } catch (error) {
    console.warn('Failed to copy raw JSON to clipboard:', error);
    setCopied(false);
  }
};

const DetailDrawerHeader = ({
  drilledFromTile, title, subtitle, onBack, onClose,
}) => (
  <div className={style.drawerHeader}>
    <div>
      {drilledFromTile ? (
        <button
          type="button"
          className={style.drawerBack}
          onClick={onBack}
        >
          ← Back to tile
        </button>
      ) : null}
      <h5 className={style.drawerTitle}>{title}</h5>
      {subtitle ? (
        <div className={style.drawerSubtitle}>{subtitle}</div>
      ) : null}
    </div>
    <button type="button" className={style.drawerClose} onClick={onClose} aria-label="Close">
      ×
    </button>
  </div>
);

const DetailDrawerBody = ({
  detail,
  vm,
  resolvedSrc,
  overlayBoxes,
  setFocusBox,
  copied,
  onCopyRaw,
}) => (
  <div className={style.drawerBody}>
    {vm.showCrop ? (
      <CropPreview src={resolvedSrc} bbox={vm.cropBbox} />
    ) : null}

    {vm.showTile ? (
      <div className={style.drawerImage}>
        <img src={resolvedSrc} alt={detail.title || 'Tile'} />
        {overlayBoxes.length ? (
          <div className={pageStyle.boxes}>
            {overlayBoxes.map((box) => (
              <DrawerBox
                key={box.id}
                box={box}
                onSelect={setFocusBox}
              />
            ))}
          </div>
        ) : null}
      </div>
    ) : null}

    {vm.rows.length ? (
      <dl className={style.drawerRows}>
        {vm.rows.map(([key, value]) => (
          <div key={key} className={style.drawerRow}>
            <dt>{key}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    ) : null}

    {vm.raw ? (
      <div className={style.drawerRaw}>
        <div className={style.drawerRawHeader}>
          <div className={style.drawerRawLabel}>{vm.rawLabel}</div>
          <button
            type="button"
            className={style.drawerRawCopy}
            onClick={onCopyRaw}
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
        <pre>{JSON.stringify(vm.raw, null, 2)}</pre>
      </div>
    ) : null}
  </div>
);

const DetailDrawerContent = ({
  detail,
  vm,
  resolvedSrc,
  overlayBoxes,
  setFocusBox,
  onClose,
  copied,
  onCopyRaw,
}) => (
  <>
    <DetailDrawerHeader
      drilledFromTile={vm.drilledFromTile}
      title={vm.title}
      subtitle={vm.subtitle}
      onBack={() => setFocusBox(null)}
      onClose={onClose}
    />
    <DetailDrawerBody
      detail={detail}
      vm={vm}
      resolvedSrc={resolvedSrc}
      overlayBoxes={overlayBoxes}
      setFocusBox={setFocusBox}
      copied={copied}
      onCopyRaw={onCopyRaw}
    />
  </>
);

const DetailDrawer = ({ detail, onClose }) => {
  const open = Boolean(detail);
  const [focusBox, setFocusBox] = useState(null);
  const [copied, setCopied] = useState(false);
  const [resolvedSrc, setResolvedSrc] = useState(null);
  const overlayBoxes = (detail?.boxes || []).filter((b) => b?.BoundingBox);
  const vm = getDrawerViewModel(detail, focusBox, resolvedSrc);

  useResolvedTileUrl(vm.rawTileUrl, setResolvedSrc);
  useDrawerLifecycle({
    detail, open, onClose, drilledFromTile: vm.drilledFromTile, setFocusBox, setCopied,
  });

  const onCopyRaw = () => copyRawJson(vm.raw, setCopied);

  return (
    <>
      <button
        type="button"
        className={[style.drawerBackdrop, open && style.drawerBackdropOpen].filter(Boolean).join(' ')}
        onClick={onClose}
        aria-label="Close details"
        tabIndex={open ? 0 : -1}
      />
      <aside
        className={[style.drawer, open && style.drawerOpen].filter(Boolean).join(' ')}
        aria-hidden={!open}
        role="dialog"
        aria-modal="true"
        aria-label={vm.title || 'Details'}
      >
        {detail ? (
          <DetailDrawerContent
            detail={detail}
            vm={vm}
            resolvedSrc={resolvedSrc}
            overlayBoxes={overlayBoxes}
            setFocusBox={setFocusBox}
            onClose={onClose}
            copied={copied}
            onCopyRaw={onCopyRaw}
          />
        ) : null}
      </aside>
    </>
  );
};

export default DetailDrawer;
